type SummaryEntry = string | { text?: string; citation_refs?: string[] }

const MAX_BULLETS = 5
const MAX_PARAGRAPHS = 3

const clean = (value?: string | null): string =>
  (value || '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export interface BulletWithSources {
  text: string
  sourceIds?: string[]
}

export interface ExecutiveSummaryCopy {
  bullets: BulletWithSources[]
  paragraphs: string[]
}

export function buildExecutiveSummaryCopy(
  summaryItems: SummaryEntry[] = [],
  narrativeParagraphs: string[] = [],
  fallbackParagraph?: string,
): ExecutiveSummaryCopy {
  // Extract bullets with their citation references
  const bulletsWithSources: BulletWithSources[] = summaryItems
    .slice(0, MAX_BULLETS)
    .map(item => {
      if (typeof item === 'string') {
        return { text: clean(item), sourceIds: undefined }
      }
      return {
        text: clean(item?.text || ''),
        sourceIds: item?.citation_refs?.filter(Boolean),
      }
    })
    .filter(b => b.text)

  // Build paragraphs - prefer narrative paragraphs, fall back to remaining summary items
  const paragraphs: string[] = narrativeParagraphs
    .map(clean)
    .filter(Boolean)
    .slice(0, MAX_PARAGRAPHS)

  // If we don't have enough paragraphs, use remaining summary items
  const remainingSummary = summaryItems.slice(MAX_BULLETS)
    .map(item => typeof item === 'string' ? clean(item) : clean(item?.text || ''))
    .filter(Boolean)

  if (paragraphs.length < MAX_PARAGRAPHS && remainingSummary.length > 0) {
    const additionalParagraph = remainingSummary.slice(0, 3).join(' ')
    if (additionalParagraph && !paragraphs.includes(additionalParagraph)) {
      paragraphs.push(additionalParagraph)
    }
  }

  // Use fallback if still not enough
  if (paragraphs.length === 0 && fallbackParagraph) {
    paragraphs.push(fallbackParagraph)
  }

  return {
    bullets: bulletsWithSources,
    paragraphs: paragraphs.slice(0, MAX_PARAGRAPHS).filter(Boolean),
  }
}

export interface StrategicInsight {
  headline: string
  insight: string
  recommendation?: string
  sourceIds?: string[]
}

export function extractStrategicInsight(
  callouts: string[] = [],
  recommendations: string[] = [],
): StrategicInsight | null {
  if (callouts.length === 0 && recommendations.length === 0) return null

  // Combine callout and recommendation into one cohesive strategic insight
  const primaryCallout = callouts[0] || ''
  const primaryRec = recommendations[0] || ''
  
  // Parse headline from recommendation if it has a colon
  let headline = 'Strategic Insight'
  let insight = primaryCallout
  let recommendation = primaryRec
  
  // If recommendation has a headline format (text before colon), extract it
  const colonMatch = primaryRec.match(/^([^:]+):\s*([\s\S]+)$/)
  if (colonMatch) {
    headline = clean(colonMatch[1])
    recommendation = clean(colonMatch[2])
  }

  // If no callout but we have recommendation, use recommendation as insight
  if (!insight && recommendation) {
    insight = recommendation
    recommendation = ''
  }

  return {
    headline,
    insight: clean(insight),
    recommendation: recommendation ? clean(recommendation) : undefined,
  }
}

export function formatTriggerLabel(raw?: string | null): string {
  if (!raw) return 'Trigger'
  const trimmed = raw.trim()
  if (!trimmed) return 'Trigger'

  let label = trimmed.replace(/[_]+/g, ' ')
  label = label.replace(/^gt\s*(\d+)/i, (_match, code) => `GT-${code} `)

  return label
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Parse structured report sections to extract Executive Overview content
 */
export interface ParsedReportContent {
  keyFindings: BulletWithSources[]
  executiveSummaryParagraphs: string[]
  strategicInsight: StrategicInsight | null
  implicationsByStakeholder: ImplicationGroup[]
  keyUncertainties: string[]  // Separate field for actual uncertainties/risks
}

export interface ImplicationGroup {
  audience: string
  items: string[]
}

// Generic interface for report sections
interface ReportSectionData {
  id?: string
  title?: string
  content_blocks?: Array<{
    type?: string
    content?: string
    items?: string[]
    citation_refs?: string[]
  }>
  subsections?: ReportSectionData[]
}

/**
 * Extract Executive Overview content from structured report sections
 * This is the primary method - uses the actual section data from the CIG/SIB
 */
export function extractFromReportSections(sections: ReportSectionData[] = []): ParsedReportContent {
  const result: ParsedReportContent = {
    keyFindings: [],
    executiveSummaryParagraphs: [],
    strategicInsight: null,
    implicationsByStakeholder: [],
    keyUncertainties: [],
  }

  if (!sections || !Array.isArray(sections)) return result

  // Find sections by title matching
  const findSection = (keywords: string[]): ReportSectionData | undefined => {
    return sections.find(section => {
      const title = (section.title || '').toLowerCase()
      return keywords.some(kw => title.includes(kw))
    })
  }

  // Extract bullets from a section's content_blocks
  const extractBulletsFromSection = (section: ReportSectionData): BulletWithSources[] => {
    const bullets: BulletWithSources[] = []
    
    for (const block of section.content_blocks || []) {
      // List blocks
      if (block.type === 'list' && block.items) {
        for (const item of block.items) {
          if (item && item.length > 20) {
            bullets.push({
              text: clean(item),
              sourceIds: block.citation_refs,
            })
          }
        }
      }
      // Paragraph blocks - parse for embedded bullets or extract key sentences
      else if (block.type === 'paragraph' && block.content) {
        const content = block.content
        
        // Check if content has multiple lines (might contain bullet points)
        const lines = content.split('\n').map(l => l.trim()).filter(Boolean)
        
        for (const line of lines) {
          // Match various bullet formats: -, *, •, 1), 1., a), etc.
          const bulletMatch = line.match(/^[-*•]\s*(.+)$/) || 
                              line.match(/^\d+[.)]\s*(.+)$/) ||
                              line.match(/^[a-z][.)]\s*(.+)$/i)
          
          if (bulletMatch && bulletMatch[1].length > 20) {
            bullets.push({
              text: clean(bulletMatch[1]),
              sourceIds: block.citation_refs,
            })
          }
        }
        
        // If no bullets found but content is substantial, look for inline numbered items
        if (content.length > 100) {
          // Pattern: "Key actions: 1) ... 2) ... 3) ..." or "1. ... 2. ... 3. ..."
          // Match numbered items: "1)" or "1." followed by content until next number or end
          const inlineNumberedPattern = /(\d+)[.)]\s*([^0-9]+?)(?=\d+[.)]|$)/g
          let match
          while ((match = inlineNumberedPattern.exec(content)) !== null) {
            const itemText = match[2].trim()
            if (itemText.length > 20 && !bullets.some(b => b.text.includes(itemText.substring(0, 30)))) {
              bullets.push({ text: clean(itemText), sourceIds: block.citation_refs })
            }
          }
        }
      }
      // Callout blocks often have key insights
      else if (block.type === 'callout' && block.content) {
        if (block.content.length > 30) {
          bullets.push({
            text: clean(block.content),
            sourceIds: block.citation_refs,
          })
        }
      }
    }
    
    // Also check subsections
    for (const subsection of section.subsections || []) {
      bullets.push(...extractBulletsFromSection(subsection))
    }
    
    return bullets
  }

  // Extract paragraphs from a section
  const extractParagraphsFromSection = (section: ReportSectionData, minLength = 80): string[] => {
    const paragraphs: string[] = []
    
    for (const block of section.content_blocks || []) {
      if (block.type === 'paragraph' && block.content) {
        const content = clean(block.content)
        // Include paragraphs above minimum length, not starting with bullets/numbers
        if (content.length > minLength && !content.match(/^[-*•\d]/)) {
          paragraphs.push(content)
        }
      }
    }
    
    // Also check subsections for paragraphs
    for (const subsection of section.subsections || []) {
      paragraphs.push(...extractParagraphsFromSection(subsection, minLength))
    }
    
    return paragraphs
  }
  
  // Get ALL paragraph content from ALL sections (for comprehensive extraction)
  const getAllParagraphs = (): string[] => {
    const allParagraphs: string[] = []
    for (const section of sections) {
      allParagraphs.push(...extractParagraphsFromSection(section, 60))
    }
    return allParagraphs
  }

  // Get bullets from sections that are appropriate for Key Findings (exclude risks, recommendations, etc.)
  const getKeyFindingsBullets = (): BulletWithSources[] => {
    const allBullets: BulletWithSources[] = []
    
    // Section titles to EXCLUDE from key findings
    const excludeSectionKeywords = [
      'risk', 'recommendation', 'mitigation', 'action', 'next step', 
      'limitation', 'methodology', 'appendix', 'reference', 'source', 
      'uncertainty', 'assumption'
    ]
    
    for (const section of sections) {
      const sectionTitle = (section.title || '').toLowerCase()
      
      // Skip sections that shouldn't contain key findings
      if (excludeSectionKeywords.some(kw => sectionTitle.includes(kw))) {
        continue
      }
      
      allBullets.push(...extractBulletsFromSection(section))
    }
    return allBullets
  }
  
  // Filter bullets to exclude those that look like recommendations/risks/mitigations or are just URLs
  const isValidKeyFinding = (bullet: BulletWithSources): boolean => {
    const text = bullet.text
    const lowerText = text.toLowerCase()
    
    // CRITICAL: Filter out raw URLs - these are not valid key findings
    // Match URLs at the start or if the text is predominantly a URL
    if (/^https?:\/\//i.test(text.trim())) return false
    if (/^\[?https?:\/\/[^\s\]]+\]?\s*$/.test(text.trim())) return false
    // If the text is mostly a URL (more than 70% of content is URL)
    const urlMatch = text.match(/https?:\/\/[^\s\]]+/g)
    if (urlMatch) {
      const urlLength = urlMatch.join('').length
      if (urlLength > text.length * 0.7) return false
    }
    // Filter out markdown link syntax that's just a URL with anchor text
    if (/^\[[^\]]+\]\(https?:\/\/[^)]+\)\s*$/.test(text.trim())) return false
    
    // Patterns that indicate this is NOT a key finding
    const excludePatterns = [
      /^[*_]?recommendation[*_]?:/i,
      /^[*_]?mitigation[*_]?:/i,
      /^[*_]?action[*_]?:/i,
      /^[*_]?risk[*_]?:/i,
      /^[*_]?next step/i,
      /^[*_]?limitation[*_]?:/i,
      /^[*_]?uncertainty[*_]?:/i,
      /\(.*likelihood.*\)/i,  // Risk probability patterns
      /\(.*impact.*\)/i,      // Risk impact patterns
      /^\*[^*]+\*$/,          // Just an emphasized title with no content
    ]
    
    for (const pattern of excludePatterns) {
      if (pattern.test(text)) return false
    }
    
    // Check if text starts with asterisk emphasis (like *Recommendation:* or **Recommendation:**)
    if (/^\*{1,2}[^*]+\*{1,2}:?\s*$/.test(text)) return false
    if (/^\*{1,2}[^*]+\*{1,2}:\s/.test(text)) {
      // Check if the emphasized part is a category we want to exclude
      const emphMatch = text.match(/^\*{1,2}([^*]+)\*{1,2}:/)
      if (emphMatch) {
        const emphText = emphMatch[1].toLowerCase()
        if (['recommendation', 'mitigation', 'action', 'risk', 'limitation', 'uncertainty', 'next step'].some(kw => emphText.includes(kw))) {
          return false
        }
      }
    }
    
    // Exclude if too short
    if (text.length < 40) return false
    
    return true
  }

  // 1. Extract Key Findings (try multiple section names)
  const keyFindingsSection = findSection(['key finding', 'key takeaway', 'main finding', 'critical finding', 'findings'])
  if (keyFindingsSection) {
    result.keyFindings = extractBulletsFromSection(keyFindingsSection)
      .filter(isValidKeyFinding)
      .slice(0, 5)
  }

  // If no key findings section, try Executive Summary for bullets
  if (result.keyFindings.length < 3) {
    const execSection = findSection(['executive summary', 'executive overview', 'summary'])
    if (execSection) {
      const execBullets = extractBulletsFromSection(execSection).filter(isValidKeyFinding)
      for (const b of execBullets) {
        if (result.keyFindings.length < 5 && !result.keyFindings.some(kb => kb.text === b.text)) {
          result.keyFindings.push(b)
        }
      }
    }
  }

  // If still not enough, get bullets from appropriate sections only
  if (result.keyFindings.length < 3) {
    const allBullets = getKeyFindingsBullets()
    // Prioritize unique, substantial bullets that are valid key findings
    const seenTexts = new Set(result.keyFindings.map(b => b.text.toLowerCase()))
    for (const b of allBullets) {
      const lowerText = b.text.toLowerCase()
      if (!seenTexts.has(lowerText) && b.text.length > 50 && isValidKeyFinding(b)) {
        result.keyFindings.push(b)
        seenTexts.add(lowerText)
        if (result.keyFindings.length >= 5) break
      }
    }
  }

  // 2. Extract Executive Summary Paragraphs
  const execSummarySection = findSection(['executive summary', 'executive overview', 'overview', 'summary'])
  if (execSummarySection) {
    result.executiveSummaryParagraphs = extractParagraphsFromSection(execSummarySection).slice(0, 3)
  }

  // Also try to get paragraphs from Background or In-Depth Analysis
  if (result.executiveSummaryParagraphs.length < 2) {
    const backgroundSection = findSection(['background', 'context', 'introduction', 'in-depth', 'analysis'])
    if (backgroundSection) {
      const bgParagraphs = extractParagraphsFromSection(backgroundSection)
      for (const p of bgParagraphs) {
        if (result.executiveSummaryParagraphs.length < 3 && !result.executiveSummaryParagraphs.includes(p)) {
          result.executiveSummaryParagraphs.push(p)
        }
      }
    }
  }

  // If still not enough paragraphs, get from all sections
  if (result.executiveSummaryParagraphs.length < 2) {
    const allParagraphs = getAllParagraphs()
    const seenParas = new Set(result.executiveSummaryParagraphs.map(p => p.substring(0, 50)))
    for (const p of allParagraphs) {
      const paraKey = p.substring(0, 50)
      if (!seenParas.has(paraKey) && p.length > 100) {
        result.executiveSummaryParagraphs.push(p)
        seenParas.add(paraKey)
        if (result.executiveSummaryParagraphs.length >= 3) break
      }
    }
  }

  // 3. Extract Strategic Insight/Recommendations
  const strategicSection = findSection(['strategic recommendation', 'recommendation', 'strategic implication', 'actionable'])
  if (strategicSection) {
    const bullets = extractBulletsFromSection(strategicSection)
    const paragraphs = extractParagraphsFromSection(strategicSection)
    
    if (bullets.length > 0 || paragraphs.length > 0) {
      result.strategicInsight = {
        headline: strategicSection.title || 'Strategic Recommendations',
        insight: paragraphs[0] || (bullets[0]?.text) || '',
        recommendation: bullets.length > 1 
          ? bullets.slice(1, 4).map(b => b.text).join(' ')
          : (paragraphs[1] || undefined),
      }
    }
  }

  // 4. Extract Implications by Stakeholder
  const stakeholderKeywords = [
    { audience: 'Investors', keywords: ['investor', 'investment'] },
    { audience: 'Executives', keywords: ['executive', 'leader', 'management'] },
    { audience: 'Researchers', keywords: ['researcher', 'academic', 'scientific'] },
    { audience: 'Policymakers', keywords: ['policy', 'regulatory', 'government'] },
    { audience: 'General', keywords: ['general audience', 'broader', 'everyone'] },
  ]

  for (const stakeholder of stakeholderKeywords) {
    const section = findSection(stakeholder.keywords)
    if (section) {
      const bullets = extractBulletsFromSection(section)
      const paragraphs = extractParagraphsFromSection(section)
      
      const items = bullets.length > 0 
        ? bullets.slice(0, 4).map(b => b.text)
        : paragraphs.slice(0, 2)
      
      if (items.length > 0) {
        result.implicationsByStakeholder.push({
          audience: stakeholder.audience,
          items,
        })
      }
    }
  }

  // If no stakeholder-specific implications, look for general implications section
  if (result.implicationsByStakeholder.length === 0) {
    const implSection = findSection(['implication', 'impact', 'consequence', 'outlook'])
    if (implSection) {
      const bullets = extractBulletsFromSection(implSection)
      const paragraphs = extractParagraphsFromSection(implSection)
      
      // Try to auto-categorize by content
      const investorItems: string[] = []
      const executiveItems: string[] = []
      const generalItems: string[] = []

      const allItems = [...bullets.map(b => b.text), ...paragraphs]
      
      for (const item of allItems) {
        const lower = item.toLowerCase()
        if (lower.includes('invest') || lower.includes('market') || lower.includes('stock') || lower.includes('portfolio') || lower.includes('return')) {
          investorItems.push(item)
        } else if (lower.includes('leader') || lower.includes('executive') || lower.includes('manage') || lower.includes('strateg') || lower.includes('decision')) {
          executiveItems.push(item)
        } else {
          generalItems.push(item)
        }
      }

      if (investorItems.length > 0) {
        result.implicationsByStakeholder.push({ audience: 'Investors', items: investorItems.slice(0, 3) })
      }
      if (executiveItems.length > 0) {
        result.implicationsByStakeholder.push({ audience: 'Executives', items: executiveItems.slice(0, 3) })
      }
      if (generalItems.length > 0) {
        result.implicationsByStakeholder.push({ audience: 'Strategic', items: generalItems.slice(0, 3) })
      }
    }
  }

  return result
}

/**
 * Parse deep research markdown to extract structured Executive Overview content
 * 
 * DESIGN PRINCIPLES (Ezra Klein style):
 * - Key Findings: 5 strong, data-backed bullet points that tell the story
 * - Executive Summary: Clear narrative paragraphs that synthesize the research
 * - Each finding should have a bold topic label followed by a substantive insight
 * - Avoid jargon; prioritize clarity and impact
 */
export function parseDeepResearchMarkdown(markdown?: string): ParsedReportContent {
  const result: ParsedReportContent = {
    keyFindings: [],
    executiveSummaryParagraphs: [],
    strategicInsight: null,
    implicationsByStakeholder: [],
    keyUncertainties: [],
  }

  if (!markdown || typeof markdown !== 'string' || markdown.length < 200) return result

  // Helper: Extract text between two patterns or to end
  const extractSection = (start: RegExp, end?: RegExp): string => {
    const startMatch = markdown.match(start)
    if (!startMatch) return ''
    const startIdx = startMatch.index! + startMatch[0].length
    if (end) {
      const endMatch = markdown.slice(startIdx).match(end)
      if (endMatch) {
        return markdown.slice(startIdx, startIdx + endMatch.index!).trim()
      }
    }
    // Return next 4000 chars or to end
    return markdown.slice(startIdx, startIdx + 4000).trim()
  }

  // Helper: Extract ALL sections matching a pattern (for multiple stakeholder sections)
  const extractAllSections = (pattern: RegExp): { header: string; content: string }[] => {
    const sections: { header: string; content: string }[] = []
    const regex = new RegExp(pattern.source, pattern.flags + (pattern.flags.includes('g') ? '' : 'g'))
    let match: RegExpExecArray | null
    
    while ((match = regex.exec(markdown)) !== null) {
      const startIdx = match.index + match[0].length
      // Find next section header (## or **Header**)
      const nextHeaderMatch = markdown.slice(startIdx).match(/\n##\s+[A-Z]|\n\*\*[A-Z][^*]{3,50}\*\*/)
      const endIdx = nextHeaderMatch 
        ? startIdx + nextHeaderMatch.index! 
        : Math.min(startIdx + 2000, markdown.length)
      
      sections.push({
        header: match[0].replace(/[#*]/g, '').trim(),
        content: markdown.slice(startIdx, endIdx).trim()
      })
    }
    return sections
  }

  // Helper: Extract bullet points from text (supports -, *, •, and bold prefix patterns)
  const extractBullets = (text: string): BulletWithSources[] => {
    const bullets: BulletWithSources[] = []
    const lines = text.split('\n')
    
    for (const line of lines) {
      const trimmed = line.trim()
      
      // Match standard bullets: - Item, * Item, • Item with **Bold Title:** pattern
      let bulletMatch = trimmed.match(/^[-*•]\s+\*\*([^*]+)\*\*[:\s]*(.*)$/)
      if (bulletMatch) {
        // Strip trailing colons from title, leading colons from content
        const title = bulletMatch[1].trim().replace(/[:\s]+$/, '')
        const content = bulletMatch[2].trim().replace(/^[:\s]+/, '')
        const fullText = `${title}: ${content}`.replace(/:\s*$/, '')
        if (fullText.length > 30) {
          bullets.push({ text: clean(fullText) })
        }
        continue
      }
      
      // Match regular bullets
      bulletMatch = trimmed.match(/^[-*•]\s+(.+)$/)
      if (bulletMatch && bulletMatch[1].length > 30) {
        bullets.push({ text: clean(bulletMatch[1]) })
        continue
      }
      
      // Match numbered bullets: 1. Item, 1) Item
      bulletMatch = trimmed.match(/^\d+[.)]\s+(.+)$/)
      if (bulletMatch && bulletMatch[1].length > 30) {
        bullets.push({ text: clean(bulletMatch[1]) })
      }
    }
    
    return bullets
  }

  // Helper: Extract paragraphs (non-bullet, substantial text blocks)
  const extractParagraphs = (text: string, minLength = 100): string[] => {
    return text
      .split(/\n\n+/)
      .map(p => p.trim().replace(/\n/g, ' ').replace(/\s+/g, ' '))
      .filter(p => {
        if (p.length < minLength) return false
        if (p.match(/^[-*•\d#]/)) return false // Skip bullets/headers
        if (p.match(/^\*\*[^*]+\*\*:/)) return false // Skip bold-prefixed items
        return true
      })
      .map(p => clean(p))
  }

  // ============================================================================
  // 1. EXTRACT KEY FINDINGS - Ezra Klein style: 5 strong, data-backed bullets
  // Priority: "Key Metrics & Facts" section with **Bold Topic:** format
  // ============================================================================
  
  // Extract "Key Metrics & Facts" or similar sections - these have the best bullet points
  const keyMetricsPatterns = [
    /\*\*Key Metrics\s*[&]\s*Facts[:\s]?\*\*|##\s*Key Metrics\s*[&]\s*Facts/i,
    /\*\*Key Metrics[:\s]?\*\*|##\s*Key Metrics/i,
    /Key Metrics & Facts:/i,
  ]
  
  let keyMetricsContent = ''
  for (const pattern of keyMetricsPatterns) {
    keyMetricsContent = extractSection(
      pattern,
      /\*\*(?:Critical Uncertainties|Dissenting|Confidence|Executive Summary|$)/i
    )
    if (keyMetricsContent && keyMetricsContent.length > 100) break
  }
  
  // Parse Key Metrics content for bold-titled findings
  // Format: "**Bold Title:** Content with details and citations"
  if (keyMetricsContent) {
    const findingPattern = /\*\*([^*]+)\*\*[:\s]+([^*\n]+(?:\n(?!\*\*|\n)[^\n]+)*)/g
    let match
    
    while ((match = findingPattern.exec(keyMetricsContent)) !== null) {
      if (result.keyFindings.length >= 5) break
      
      // Clean title and remove any trailing colons (regex may capture them)
      const title = clean(match[1]).replace(/[:\s]+$/, '').trim()
      let content = clean(match[2])
      
      // Remove citation markers like ([1]) and clean up
      content = content.replace(/\s*\(\[?\d+\]?\)\s*/g, ' ').replace(/\s*\[\d+\]\s*/g, ' ').trim()
      // Remove leading colons that may have been left from parsing
      content = content.replace(/^[:\s]+/, '').trim()
      
      // Skip if too short or looks like a header
      if (content.length < 30 || title.length > 100) continue
      
      // Format as "Title: Content" in Ezra Klein style (clear, impactful)
      const formattedFinding = `${title}: ${content}`
      
      // Check for duplicates
      const isDuplicate = result.keyFindings.some(kf => {
        const existingLower = kf.text.toLowerCase()
        const newLower = formattedFinding.toLowerCase()
        return existingLower.includes(newLower.substring(0, 40)) || 
               newLower.includes(existingLower.substring(0, 40))
      })
      
      if (!isDuplicate && formattedFinding.length > 50) {
        result.keyFindings.push({ text: formattedFinding })
      }
    }
  }
  
  // Also try bullet-formatted key findings if we don't have enough
  if (result.keyFindings.length < 5 && keyMetricsContent) {
    const bullets = extractBullets(keyMetricsContent)
    for (const bullet of bullets) {
      if (result.keyFindings.length >= 5) break
      
      // Check for duplicates
      const isDuplicate = result.keyFindings.some(kf => {
        const existingLower = kf.text.toLowerCase()
        const newLower = bullet.text.toLowerCase()
        return existingLower.includes(newLower.substring(0, 40)) || 
               newLower.includes(existingLower.substring(0, 40))
      })
      
      if (!isDuplicate && bullet.text.length > 50) {
        result.keyFindings.push(bullet)
      }
    }
  }
  
  // Fallback: Try extracting from BLUF if still not enough findings
  if (result.keyFindings.length < 3) {
    const blufContent = extractSection(
      /\*\*(?:BLUF|Bottom Line Up Front)[:\s]?\*\*|##\s*(?:BLUF|Bottom Line)/i,
      /\*\*(?:Key Metrics|Operational|Critical|$)/i
    )
    
    if (blufContent) {
      // Extract complete sentences from BLUF
      const sentences = blufContent
        .replace(/([.!?])\s+/g, '$1|||')
        .split('|||')
        .map(s => clean(s))
        .filter(s => {
          if (s.length < 60 || s.length > 300) return false
          if (!/[.!?]$/.test(s)) return false
          if (/^[a-z]/.test(s)) return false
          // Good: Contains data or key insights
          return /\d+%?|\$|million|billion|key|critical|significant|evidence|shows|demonstrates/i.test(s)
        })
      
      for (const sentence of sentences) {
        if (result.keyFindings.length >= 5) break
        
        const isDuplicate = result.keyFindings.some(kf => 
          kf.text.toLowerCase().includes(sentence.toLowerCase().substring(0, 40))
        )
        
        if (!isDuplicate) {
          result.keyFindings.push({ text: sentence })
        }
      }
    }
  }

  // ============================================================================
  // 2. EXTRACT EXECUTIVE SUMMARY - Ezra Klein style narrative
  // The BLUF (Bottom Line Up Front) section provides the core thesis
  // We want clear, synthesized paragraphs that tell the story accessibly
  // ============================================================================
  
  // Helper to clean and enhance paragraph quality
  const enhanceParagraph = (text: string): string => {
    let enhanced = clean(text)
    // Remove excessive citations like ([1]) ([2]) from the middle of text
    enhanced = enhanced.replace(/\s*\(\[?\d+\]?\)\s*/g, ' ')
    enhanced = enhanced.replace(/\s*\[\d+\]\s*/g, ' ')
    
    // Remove query text that may be appended to the content
    const queryStartPatterns = [
      /assess the (?:business )?viability.*/i,
      /i want to understand.*/i,
      /i want to know.*/i,
      /i would like to.*/i,
      /i need to understand.*/i,
      /where the source would be.*/i,
      /where would (?:the )?be.*/i,
      /help me understand.*/i,
      /please analyze.*/i,
      /please research.*/i,
      /looking to understand.*/i,
      /can you (?:help|explain|analyze|research).*/i,
      /what (?:are|is) the (?:best|main|key).*/i,
      /to be consumed internally.*/i,
      /exported internationally.*/i,
    ]
    
    for (const pattern of queryStartPatterns) {
      enhanced = enhanced.replace(pattern, '')
    }
    
    // Clean up double spaces and trailing punctuation issues
    enhanced = enhanced.replace(/\s+/g, ' ')
    enhanced = enhanced.replace(/\.\s*$/, '.') // Ensure clean ending
    enhanced = enhanced.replace(/,\s*$/, '.') // Fix trailing comma
    return enhanced.trim()
  }
  
  // Helper to detect if text looks like a user query (should be excluded)
  const looksLikeUserQuery = (text: string): boolean => {
    const queryPatterns = [
      /i want to understand/i,
      /i want to know/i,
      /i would like to/i,
      /help me understand/i,
      /can you tell me/i,
      /looking to understand/i,
      /assess the.*viability/i,
      /please analyze/i,
      /where would be/i,
      /where do i begin/i,
      /i'm an.*entrepreneur/i,
      /looking to launch/i,
      /how should i start/i,
      /nearing retirement/i,
      /i'm looking to/i,
      /i am looking to/i,
    ]
    
    for (const pattern of queryPatterns) {
      if (pattern.test(text)) return true
    }
    
    if (/^i\s+(want|would|need|am|have)/i.test(text)) return true
    if (/^i'm\s/i.test(text)) return true
    if (/how\s+(should|can|do)\s+i/i.test(text)) return true
    
    return false
  }
  
  // Score paragraphs for Ezra Klein narrative quality
  const scoreParagraphQuality = (p: string): number => {
    if (looksLikeUserQuery(p)) return -100
    
    let score = 0
    const lower = p.toLowerCase()
    
    // Good: Clear thesis-like opening
    if (/^(the|this|cities can|evidence|technology|pricing|a well-designed)/i.test(p)) score += 3
    
    // Good: Contains transitional/analytical phrases (Ezra Klein style)
    if (/however|moreover|the key|the challenge|success hinges|provided|if implemented/i.test(lower)) score += 3
    
    // Good: Contains specific data (makes argument concrete)
    if (/\d+%|\d+\s*(tons?|million|billion|dollars?|\$|day|year)/i.test(p)) score += 2
    
    // Good: Right length for a narrative paragraph (150-500 chars)
    if (p.length >= 150 && p.length <= 500) score += 3
    else if (p.length > 500 && p.length <= 700) score += 2
    else if (p.length > 700) score += 1
    else if (p.length < 100) score -= 2
    
    // Good: Contains analytical/synthesis language
    if (/demonstrates|validates|reveals|suggests|indicates|the evidence|the analysis|research shows/i.test(lower)) score += 2
    
    // Good: Has clear cause-effect or conditional structure
    if (/can|if|when|provided|requires|hinges on/i.test(lower)) score += 1
    
    // Bad: Too many semicolons (list-like, not narrative)
    if ((p.match(/;/g) || []).length > 3) score -= 2
    
    // Bad: Starts with bullet-like content
    if (/^[-*•\d]/.test(p)) score -= 3
    
    return score
  }
  
  const potentialParagraphs: { text: string; score: number }[] = []
  
  // PRIMARY: Extract from BLUF - this contains the core thesis
  const blufSectionPatterns = [
    /\*\*(?:BLUF|Bottom Line Up Front)[:\s]?\*\*/i,
    /##\s*(?:BLUF|Bottom Line Up Front)/i,
    /Bottom Line Up Front.*?:/i,
  ]
  
  let blufContent = ''
  for (const pattern of blufSectionPatterns) {
    blufContent = extractSection(
      pattern,
      /\*\*(?:Key Metrics|Critical Uncertainties|$)/i
    )
    if (blufContent && blufContent.length > 100) break
  }
  
  if (blufContent) {
    // The BLUF often contains the entire thesis in one paragraph - don't split unnecessarily
    const fullBluf = enhanceParagraph(blufContent)
    if (fullBluf.length > 100 && !looksLikeUserQuery(fullBluf)) {
      // If BLUF is a good length (100-800 chars), use it as the primary paragraph
      if (fullBluf.length <= 800) {
        potentialParagraphs.push({ text: fullBluf, score: scoreParagraphQuality(fullBluf) + 15 })
      } else {
        // If too long, extract the first portion as a complete thought
        const sentences = fullBluf
          .replace(/([.!?])\s+/g, '$1|||')
          .split('|||')
          .filter(s => s.trim().length > 30)
        
        // Take first 3-4 sentences (usually ~300-500 chars)
        const firstPortion = sentences.slice(0, 4).join(' ').trim()
        if (firstPortion.length > 100) {
          potentialParagraphs.push({ text: firstPortion, score: scoreParagraphQuality(firstPortion) + 15 })
        }
        
        // Also add the next portion as a second paragraph
        if (sentences.length > 4) {
          const secondPortion = sentences.slice(4, 7).join(' ').trim()
          if (secondPortion.length > 80) {
            potentialParagraphs.push({ text: secondPortion, score: scoreParagraphQuality(secondPortion) + 12 })
          }
        }
      }
    }
    
    // Also extract any sub-paragraphs from BLUF that may exist
    const blufParas = extractParagraphs(blufContent, 80)
    for (const p of blufParas) {
      const enhanced = enhanceParagraph(p)
      if (enhanced.length > 80) {
        potentialParagraphs.push({ text: enhanced, score: scoreParagraphQuality(enhanced) + 10 })
      }
    }
  }
  
  // SECONDARY: Try "Executive Summary with Policy Implications" or similar
  const execSummaryPatterns = [
    /##\s*Executive Summary(?:\s+with\s+Policy\s+Implications)?/i,
    /\*\*Executive Summary\*\*/i,
    /Executive Summary with Policy Implications/i,
  ]
  
  let execContent = ''
  for (const pattern of execSummaryPatterns) {
    execContent = extractSection(
      pattern,
      /##\s*(?:Key Metrics|Background|Critical|$)|\*\*(?:Key Metrics|BLUF)/i
    )
    if (execContent && execContent.length > 100) break
  }
  
  if (execContent) {
    // First, try to get the intro paragraph (first substantial block of text)
    const execLines = execContent.split('\n').filter(l => l.trim().length > 0)
    const introParagraph: string[] = []
    for (const line of execLines) {
      const trimmed = line.trim()
      // Stop at bullets, headers, or bold section markers
      if (/^[-*•\d#]/.test(trimmed) || /^\*\*[A-Z]/.test(trimmed)) break
      if (trimmed.length > 30) introParagraph.push(trimmed)
      if (introParagraph.join(' ').length > 400) break
    }
    
    if (introParagraph.length > 0) {
      const combined = enhanceParagraph(introParagraph.join(' '))
      if (combined.length > 100 && !looksLikeUserQuery(combined)) {
        potentialParagraphs.push({ text: combined, score: scoreParagraphQuality(combined) + 8 })
      }
    }
    
    // Also extract structured paragraphs
    const execParas = extractParagraphs(execContent, 80)
    for (const p of execParas) {
      const enhanced = enhanceParagraph(p)
      if (enhanced.length > 80) {
        potentialParagraphs.push({ text: enhanced, score: scoreParagraphQuality(enhanced) + 5 })
      }
    }
  }
  
  // TERTIARY: Try "Confidence Assessment" section for additional insight
  const confidenceContent = extractSection(
    /\*\*Confidence Assessment[:\s]?\*\*|##\s*Confidence Assessment/i,
    /\*\*(?:Critical Uncertainties|Dissenting|$)|##\s*(?:Critical|Dissenting)/i
  )
  if (confidenceContent) {
    const confParas = extractParagraphs(confidenceContent, 100)
    for (const p of confParas.slice(0, 2)) {
      const enhanced = enhanceParagraph(p)
      if (enhanced.length > 100) {
        potentialParagraphs.push({ text: enhanced, score: scoreParagraphQuality(enhanced) + 3 })
      }
    }
  }
  
  // Sort by quality score and pick top 3 unique paragraphs
  const sortedParagraphs = potentialParagraphs
    .filter(p => p.score > -50)
    .sort((a, b) => b.score - a.score)
  const usedParaStarts = new Set<string>()
  
  for (const para of sortedParagraphs) {
    if (result.executiveSummaryParagraphs.length >= 3) break
    
    if (looksLikeUserQuery(para.text)) continue
    
    // Check for duplicate starts (first 60 chars)
    const paraStart = para.text.substring(0, 60).toLowerCase()
    if (usedParaStarts.has(paraStart)) continue
    
    // Check similarity with existing paragraphs
    const isDuplicate = result.executiveSummaryParagraphs.some(existing => {
      const existingLower = existing.toLowerCase()
      const newLower = para.text.toLowerCase()
      return existingLower.includes(newLower.substring(0, 50)) || 
             newLower.includes(existingLower.substring(0, 50))
    })
    
    if (!isDuplicate) {
      result.executiveSummaryParagraphs.push(para.text)
      usedParaStarts.add(paraStart)
    }
  }

  // 3. EXTRACT STRATEGIC RECOMMENDATIONS
  let recContent = extractSection(
    /\*\*(?:Strategic )?Recommendations?[:\s]?\*\*|##\s*(?:Strategic )?Recommendations?|##\s*Actionable/i,
    /\*\*(?:Confidence|Critical|Methodology)|##\s*(?:Confidence|Critical|Limitations)/i
  )
  
  if (recContent) {
    const recBullets = extractBullets(recContent)
    const recParas = extractParagraphs(recContent, 60)
    
    if (recBullets.length > 0 || recParas.length > 0) {
      result.strategicInsight = {
        headline: 'What Leaders Should Do',
        insight: recParas[0] || recBullets[0]?.text || '',
        recommendation: recBullets.length > 0 
          ? recBullets.slice(0, 3).map(b => b.text).join(' • ')
          : undefined,
      }
    }
  }
  
  // Fallback: Try "What Leaders Should Do"
  if (!result.strategicInsight) {
    let leadersContent = extractSection(
      /\*\*What Leaders Should Do[:\s]?\*\*|##\s*What Leaders Should Do/i,
      /\*\*Confidence Assessment|##\s*Confidence|##\s*Critical Uncertainties/i
    )
    if (leadersContent) {
      const bullets = extractBullets(leadersContent)
      const paras = extractParagraphs(leadersContent, 60)
      if (bullets.length > 0 || paras.length > 0) {
        result.strategicInsight = {
          headline: 'What Leaders Should Do',
          insight: paras[0] || bullets[0]?.text || '',
          recommendation: bullets.length > 0 ? bullets.slice(0, 3).map(b => b.text).join(' • ') : undefined,
        }
      }
    }
  }

  // 4. EXTRACT IMPLICATIONS BY STAKEHOLDER - Enhanced to find ALL stakeholders
  // Search for various formats: "For Investors:", "Implications for Investors", "## Investors", etc.
  
  const stakeholderPatterns: { audience: string; patterns: RegExp[] }[] = [
    { 
      audience: 'Executives', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Executives?|C-Suite|Leadership|Leaders?|Management)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Executives?|C-Suite|Leadership)\*\*/i,
      ]
    },
    { 
      audience: 'Investors', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Investors?|Investment|Shareholders?)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Investors?|Investment)\*\*/i,
      ]
    },
    { 
      audience: 'Policymakers', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Policy ?makers?|Regulators?|Government)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Policy ?makers?|Regulators?)\*\*/i,
      ]
    },
    { 
      audience: 'Researchers', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Researchers?|Academics?|Scientists?)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Researchers?|Academics?)\*\*/i,
      ]
    },
    { 
      audience: 'Employees', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Employees?|Workers?|Workforce|Staff)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Employees?|Workers?)\*\*/i,
      ]
    },
    { 
      audience: 'Entrepreneurs', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Entrepreneurs?|Startups?|Founders?)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Entrepreneurs?|Startups?)\*\*/i,
      ]
    },
    { 
      audience: 'Consumers', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Consumers?|Customers?|Users?|General Public)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Consumers?|Customers?)\*\*/i,
      ]
    },
    { 
      audience: 'Suppliers', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Suppliers?|Vendors?|Partners?)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Suppliers?|Vendors?)\*\*/i,
      ]
    },
    { 
      audience: 'Farmers', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Farmers?|Producers?|Growers?|Smallholders?)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Farmers?|Producers?)\*\*/i,
      ]
    },
    { 
      audience: 'Processors', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Processors?|Manufacturers?|Millers?)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Processors?|Manufacturers?)\*\*/i,
      ]
    },
    { 
      audience: 'Exporters', 
      patterns: [
        /(?:##\s*)?(?:For )?(?:Implications (?:for )?)?(?:Exporters?|Traders?|Trading Companies)/i,
        /(?:##\s*)?\*\*(?:For )?(?:Exporters?|Traders?)\*\*/i,
      ]
    },
  ]
  
  for (const { audience, patterns } of stakeholderPatterns) {
    // Skip if we already have this audience
    if (result.implicationsByStakeholder.some(imp => imp.audience === audience)) continue
    
    for (const pattern of patterns) {
      const implContent = extractSection(pattern, /##\s*[A-Z]|\*\*(?:For )?[A-Z][^*]{3,30}\*\*/)
      if (implContent && implContent.length > 50) {
        const bullets = extractBullets(implContent)
        const paras = extractParagraphs(implContent, 50)
        const items = bullets.length > 0 ? bullets.map(b => b.text) : paras
        if (items.length > 0) {
          result.implicationsByStakeholder.push({
            audience,
            items: items.slice(0, 4),
          })
          break // Found this audience, move to next
        }
      }
    }
  }
  
  // Extract Key Uncertainties/Risks - These should be clear, well-formatted risk items
  
  // Helper to check if text is predominantly a URL (should be excluded)
  const isUrl = (text: string): boolean => {
    const trimmed = text.trim()
    // Direct URL
    if (/^https?:\/\//i.test(trimmed)) return true
    // Markdown link that's just a URL
    if (/^\[[^\]]+\]\(https?:\/\/[^)]+\)\s*$/.test(trimmed)) return true
    // Text that's mostly a URL (more than 70%)
    const urlMatch = trimmed.match(/https?:\/\/[^\s\]]+/g)
    if (urlMatch) {
      const urlLength = urlMatch.join('').length
      if (urlLength > trimmed.length * 0.7) return true
    }
    return false
  }
  
  // Helper to format risk items properly
  const formatRiskItem = (text: string): string | null => {
    let cleaned = clean(text)
    if (cleaned.length < 30) return null
    
    // CRITICAL: Filter out URLs - they are not valid risk items
    if (isUrl(cleaned)) return null
    
    // If it starts with a bold title, format it properly
    const boldMatch = cleaned.match(/^\*\*([^*]+)\*\*[:\s]*(.*)$/)
    if (boldMatch) {
      // Strip trailing colons from title (regex may capture them)
      const title = boldMatch[1].trim().replace(/[:\s]+$/, '')
      let content = boldMatch[2].trim()
      // Remove leading colon from content if present
      content = content.replace(/^[:\s]+/, '').trim()
      // Skip if content is mostly a URL
      if (isUrl(content)) return null
      if (content.length > 20) {
        return `${title}: ${content}`
      }
      return cleaned
    }
    
    // Clean up the text - remove citation markers and leading colons
    cleaned = cleaned.replace(/\s*\(\[\d+\]\)\s*/g, ' ').trim()
    // Remove leading colon if present (common artifact from parsing)
    cleaned = cleaned.replace(/^[:\s]+/, '').trim()
    // Remove any trailing URL references
    cleaned = cleaned.replace(/\s*\(https?:\/\/[^)]+\)\s*$/g, '').trim()
    
    return cleaned.length > 30 ? cleaned : null
  }
  
  const allRisks: string[] = []
  
  // Try "Critical Uncertainties" section (handles various formats including "Critical Uncertainties & Risks:")
  let uncertaintiesContent = extractSection(
    /\*\*Critical Uncertainties[^*]*\*\*|##\s*Critical Uncertainties|##\s*Key Uncertainties|\*\*Key Uncertainties[^*]*\*\*/i,
    /\*\*(?:Dissenting Views|Methodology|Confidence|Strategic)|##\s*(?:Dissenting|Methodology|Confidence|Strategic)|In sum/i
  )
  if (uncertaintiesContent) {
    const bullets = extractBullets(uncertaintiesContent)
    for (const b of bullets) {
      const formatted = formatRiskItem(b.text)
      if (formatted && !allRisks.some(r => r.includes(formatted.substring(0, 40)))) {
        allRisks.push(formatted)
      }
    }
    // Also extract complete sentences if no bullets
    if (allRisks.length === 0) {
      // Try to find paragraph-style risks (look for sentences containing risk keywords)
      // First, try to extract complete risk paragraphs that start with ordinals on their own line
      const cleanedContent = clean(uncertaintiesContent)
      
      // Only split on ordinals if they appear at ACTUAL paragraph boundaries (after double newline or at start)
      // Match pattern: start of content OR double newline + ordinal word + comma/colon
      const paragraphOrdinalPattern = /(?:^|(?:\.\s+)(?=[A-Z]))(First|Second|Third|Fourth|Fifth),\s+/gi
      const ordinalMatches = cleanedContent.match(paragraphOrdinalPattern) || []
      
      // Only use ordinal splitting if we have at least 2 different ordinals (First AND Second/Third/etc)
      const hasMultipleOrdinals = ordinalMatches.length >= 2 && 
        new Set(ordinalMatches.map(m => m.toLowerCase().trim())).size >= 2
      
      if (hasMultipleOrdinals) {
        // Split on sentence boundaries that precede ordinals
        const parts = cleanedContent.split(/\.\s+(?=(?:First|Second|Third|Fourth|Fifth),)/gi)
        for (const part of parts.slice(0, 5)) {
          const formatted = formatRiskItem(part.trim())
          if (formatted && formatted.length >= 50 && !allRisks.some(r => r.includes(formatted.substring(0, 40)))) {
            allRisks.push(formatted)
          }
        }
      } 
      
      // Fallback: extract risk-related sentences as complete units
      if (allRisks.length === 0) {
        // Fallback to sentence splitting with abbreviation protection
        // Protect common abbreviations from being split
        let protected_text = clean(uncertaintiesContent)
          .replace(/\be\.g\.\s+/gi, 'eg_PROTECTED ')
          .replace(/\bi\.e\.\s+/gi, 'ie_PROTECTED ')
          .replace(/\bU\.S\.\s+/gi, 'US_PROTECTED ')
          .replace(/\bU\.K\.\s+/gi, 'UK_PROTECTED ')
          .replace(/\bE\.U\.\s+/gi, 'EU_PROTECTED ')
          .replace(/\bvs\.\s+/gi, 'vs_PROTECTED ')
          .replace(/\betc\.\s+/gi, 'etc_PROTECTED ')
          .replace(/\bDr\.\s+/gi, 'Dr_PROTECTED ')
          .replace(/\bMr\.\s+/gi, 'Mr_PROTECTED ')
          .replace(/\bMs\.\s+/gi, 'Ms_PROTECTED ')
          .replace(/\bMrs\.\s+/gi, 'Mrs_PROTECTED ')
          .replace(/\bInc\.\s+/gi, 'Inc_PROTECTED ')
          .replace(/\bCorp\.\s+/gi, 'Corp_PROTECTED ')
          .replace(/\bLtd\.\s+/gi, 'Ltd_PROTECTED ')
        
        const sentences = protected_text
          .replace(/([.!?])\s+/g, '$1|||')
          .split('|||')
          .map(s => s.trim()
            .replace(/eg_PROTECTED/g, 'e.g.')
            .replace(/ie_PROTECTED/g, 'i.e.')
            .replace(/US_PROTECTED/g, 'U.S.')
            .replace(/UK_PROTECTED/g, 'U.K.')
            .replace(/EU_PROTECTED/g, 'E.U.')
            .replace(/vs_PROTECTED/g, 'vs.')
            .replace(/etc_PROTECTED/g, 'etc.')
            .replace(/Dr_PROTECTED/g, 'Dr.')
            .replace(/Mr_PROTECTED/g, 'Mr.')
            .replace(/Ms_PROTECTED/g, 'Ms.')
            .replace(/Mrs_PROTECTED/g, 'Mrs.')
            .replace(/Inc_PROTECTED/g, 'Inc.')
            .replace(/Corp_PROTECTED/g, 'Corp.')
            .replace(/Ltd_PROTECTED/g, 'Ltd.')
          )
          .filter(s => s.length >= 50 && /[.!?]$/.test(s))
        
        for (const s of sentences.slice(0, 3)) {
          const formatted = formatRiskItem(s)
          if (formatted) {
            allRisks.push(formatted)
          }
        }
      }
    }
  }
  
  // Try "Major Risks" or "Key Risks" section
  let risksContent = extractSection(
    /\*\*(?:Major|Key|Critical) Risks?[:\s]?\*\*|##\s*(?:Major|Key|Critical) Risks?/i,
    /##\s*[A-Z]|\*\*(?:Strategic|Recommendation|Forward|Go\/No-Go)/i
  )
  if (risksContent && allRisks.length < 5) {
    const bullets = extractBullets(risksContent)
    for (const b of bullets) {
      if (allRisks.length >= 5) break
      const formatted = formatRiskItem(b.text)
      if (formatted && !allRisks.some(r => r.includes(formatted.substring(0, 40)))) {
        allRisks.push(formatted)
      }
    }
  }
  
  // Try "Limitations" section for additional risks
  let limitationsContent = extractSection(
    /\*\*Limitations[:\s]?\*\*|##\s*Limitations/i,
    /##\s*[A-Z]|\*\*(?:Appendix|Reference|Confidence)/i
  )
  if (limitationsContent && allRisks.length < 5) {
    const bullets = extractBullets(limitationsContent)
    for (const b of bullets) {
      if (allRisks.length >= 5) break
      const formatted = formatRiskItem(b.text)
      if (formatted && !allRisks.some(r => r.includes(formatted.substring(0, 40)))) {
        allRisks.push(formatted)
      }
    }
  }
  
  // Try "Viability and Sensitivities" for risk mentions
  let viabilityContent = extractSection(
    /\*\*Viability[^*]*\*\*|##\s*Viability/i,
    /\*\*(?:Market Opportunities|Go-to-Market|Risks)|##\s*(?:Market|Go-to)/i
  )
  if (viabilityContent && allRisks.length < 5) {
    const viabilityText = clean(viabilityContent)
    // Look for sentences that mention risks, challenges, or uncertainties
    const riskSentences = viabilityText.match(/[^.]*(?:risk|challenge|uncertainty|if [^,]+,|unless)[^.]+\./gi)
    if (riskSentences) {
      for (const sentence of riskSentences.slice(0, 2)) {
        if (allRisks.length >= 5) break
        const formatted = formatRiskItem(sentence)
        if (formatted && !allRisks.some(r => r.includes(formatted.substring(0, 40)))) {
          allRisks.push(formatted)
        }
      }
    }
  }
  
  result.keyUncertainties = allRisks.slice(0, 5)

  return result
}

export interface TimelineEntry {
  date?: string
  title: string
  detail?: string
  sourceIds?: string[]
  anchors?: Array<{
    source_id?: string
    source_url?: string
    locator?: string
    snippet?: string
  }>
}

export function parseTimelineEntries(blocks: any[] = []): TimelineEntry[] {
  const entries: TimelineEntry[] = []

  const pushEntry = (raw: string, blockSourceIds?: string[]) => {
    const normalizedRaw = (raw || '').replace(/\*\*/g, '').replace(/\r/g, '').trim()
    let text = clean(normalizedRaw)
    if (!text) return

    let sourceIds = Array.isArray(blockSourceIds)
      ? blockSourceIds.map((part) => clean(part)).filter(Boolean)
      : undefined
    const sourcePattern = /\((?:sources?|refs?)[:\-]\s*([^)]+)\)\s*$/i
    const sourceMatch = text.match(sourcePattern)
    if (sourceMatch) {
      text = clean(text.replace(sourceMatch[0], ''))
      const inlineSourceIds = sourceMatch[1]
        .split(',')
        .map(part => clean(part))
        .filter(Boolean)
      sourceIds = [...new Set([...(sourceIds || []), ...inlineSourceIds])]
    }

    const match = text.match(/^([0-9]{4}(?:[-/][0-9]{2}(?:[-/][0-9]{2})?)?)\s*[-–—]\s*(.+)$/)
    if (match) {
      const [, date, remainder] = match
      const rawWithoutSources = sourceMatch
        ? normalizedRaw.replace(sourcePattern, '').trim()
        : normalizedRaw
      const rawRemainder = rawWithoutSources.replace(/^([0-9]{4}(?:[-/][0-9]{2}(?:[-/][0-9]{2})?)?)\s*[-–—]\s*/, '').trim()
      const lineParts = rawRemainder
        .split(/\n{2,}|\n+/)
        .map(part => clean(part))
        .filter(Boolean)
      const [headlineFromLines, ...detailFromLines] = lineParts
      const fallbackParts = remainder.split(' - ')
      const headline = headlineFromLines || clean(fallbackParts[0]) || remainder
      const cleanedDetail = clean(
        detailFromLines.length > 0
          ? detailFromLines.join(' ')
          : fallbackParts.slice(1).join(' - ')
      )
      entries.push({
        date,
        title: headline,
        ...(cleanedDetail ? { detail: cleanedDetail } : {}),
        ...(sourceIds && sourceIds.length > 0 ? { sourceIds } : {}),
      })
      return
    }

    entries.push({
      title: text,
      ...(sourceIds && sourceIds.length > 0 ? { sourceIds } : {}),
    })
  }

  blocks.forEach((block) => {
    const blockSourceIds = Array.isArray(block?.citation_refs)
      ? block.citation_refs.filter(Boolean)
      : undefined

    if (Array.isArray(block?.items)) {
      block.items.forEach((item: string) => pushEntry(item, blockSourceIds))
      return
    }

    if (typeof block?.content === 'string') {
      pushEntry(block.content, blockSourceIds)
    }
  })

  return entries
}

